<template>
	<div class="embedded-schedule-container">
		<div v-if="days.length > 0" class="mt-5">
			<h6>UPCOMING CLASSES</h6>
			<div
				v-for="(c, index) in days"
				:key="c.id"
				class="schedule-item"
				:class="{ 'd-none d-md-flex': index > 2, 'schedule-item-3': index === 2 }"
				:data-testid="`schedule-item-${index}`"
			>
				<div class="copy">{{ c.name }}</div>
				<span class="badge">{{ c.startTime }}</span>
			</div>
		</div>
		<div class="row mt-5">
			<div class="col">
				<a
					v-adobe-data-layer="viewADLConfig"
					class="btn btn-light w-100"
					:href="buttonUrl"
					data-testid="view-full-schedule"
				>
					{{ buttonText }}
				</a>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, type Ref } from 'vue';
import { scheduleService } from '@/services/schedulesService';
import dayjs from 'dayjs';
import type {
	AempschedClass,
	AempschedDay,
	IADLConfig,
	IFilterSelection
} from 'Types/aempsched/aempschedDay';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { getAuthoredContent } from '@/utils/getAuthoredContent';
import { logError, logWarn } from '@/utils/logger';
import vAdobeDataLayer from '@/directives/AdobeDataLayer';
import { club } from '@/utils/club';
import { type IAuthoredVariation } from 'Types/embedded-schedule/embeddedSchedule';

dayjs.extend(customParseFormat);

const days: Ref<AempschedClass[]> = ref([]);
const authorConfig: Ref<IAuthoredVariation> = ref({});
const buttonUrl: Ref<string> = ref('');

const buttonText = computed(() => {
	return club.open === 'presale'
		? authorConfig.value.presaleButtonText
		: authorConfig.value.buttonText;
});

onBeforeMount(async () => {
	authorConfig.value = getAuthoredContent<IAuthoredVariation>('AempschedEmbeddedScheduleScript');
	buttonUrl.value = buildUrl(club.prospectPagePath, authorConfig.value);

	const filters =
		authorConfig?.value?.interests?.map((interest) => {
			return {
				name: interest,
				group: 'interests'
			};
		}) || [];

	try {
		const res = await scheduleService.getSchedule(
			dayjs().format('YYYY-MM-DD'),
			dayjs()
				.add(Number(authorConfig.value.daysToAdd) || 1, 'day')
				.format('YYYY-MM-DD'),
			filters as IFilterSelection[]
		);

		const dates = res.data.dates;
		if (dates.length > 1) {
			days.value = filterClasses(dates);
		} else {
			logWarn('No classes found', { dates });
		}
	} catch (error) {
		logError('GET error requesting schedules data', { authorConfig: authorConfig.value }, error);
	}
});

const filterClasses = (dates: AempschedDay[]): AempschedClass[] => {
	const today = dates[0].classes.filter((t: AempschedClass) => {
		return dayjs(t.startTime, 'HH:mm A').isAfter(dayjs());
	});

	return today.concat(dates[1].classes).slice(0, 5);
};

const buildUrl = (url: string, config: IAuthoredVariation): string => {
	if (url && config) {
		const path = url.replace(/\.html$/, '');
		const encodedFilters =
			config.interests
				?.map((interest) => {
					return `interests=${encodeURIComponent(interest)}`;
				})
				.join('&') || '';

		return `${path}/schedules.html?${encodedFilters}`;
	} else {
		return '';
	}
};

const viewADLConfig: IADLConfig = {
	name: 'crosslinkschedule',
	type: 'button',
	title: 'View Full Schedule',
	link: buttonUrl.value
};
</script>

<style lang="scss" scoped>
.schedule-item {
	border-top: solid 1px rgba(255, 255, 255, 0.7);
	display: flex;
	justify-content: space-between;
	padding-top: 1rem;
	padding-bottom: 2.75rem;

	@include media-breakpoint-down(md) {
		padding-bottom: 1rem;

		&-3 {
			border-bottom: solid 1px rgba(255, 255, 255, 0.7);
		}
	}

	&:last-child {
		border-bottom: solid 1px rgba(255, 255, 255, 0.7);
	}
}
</style>
